/**
 * Self-service account requests (ARCHITECTURE.md "Account requests").
 *
 * Public page /request-account/:
 *   const org = await listSignupOrgUnits()                   // ids + names only, no sign-in
 *   await requestAccount({ displayName, username, email, companyId, administrationId,
 *                          departmentId, note, contactMethod, contactProof })   // contactProof = ID token of the SMS / email-link sign-in
 *
 * Login page (after a failed sign-in):
 *   const notice = await accountRequestNotice(err, username)  // null, or { title, body } message keys
 *
 * Admin Requests pages (employees.create):
 *   const { requests, loading, error } = usePendingAccountRequests(enabled)
 *   const count = usePendingRequestCount(enabled)
 *   await approveAccountRequest({ uid, password, companyId, administrationId, departmentId, jobTitle, staffNumber, roleIds })
 *   await rejectAccountRequest({ uid, reason })
 *
 * Callable failures carry `details.reason`; requestErrorKey() maps it to
 * requests.errors.<reason>, else to the errors namespace.
 */
import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where, type FirestoreError, type Timestamp } from "firebase/firestore";
import { errorCode, errorMessageKey } from "../errors";
import { callable, db, SNAPSHOT_OPTIONS } from "../firebase";
import { hasMessage } from "../i18n/translate";
import { normalizeUsername, validateUsername } from "../auth-shared";
import type { LocalizedText, VerificationMethod } from "../types";
import { callableErrorDetails } from "./users";

export const ACCOUNT_REQUESTS_COLLECTION = "accountRequests";
/** Mirrors functions/src/config.ts ACCOUNT_REQUEST_NOTE_MAX_LENGTH. */
export const ACCOUNT_REQUEST_NOTE_MAX_LENGTH = 500;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AccountRequestStatus = "pending" | "approved" | "rejected";

/** accountRequests/{uid} (written only by functions/src/requests.ts). */
export interface AccountRequest {
  status: AccountRequestStatus;
  displayName: string;
  username: string;
  contactMethod: VerificationMethod;
  /** E.164 (+9665XXXXXXXX) or a lowercased email. */
  contact: string;
  contactVerifiedAt: Timestamp | null;
  /** Notification email (lowercased). */
  email: string;
  companyId: string;
  administrationId: string;
  departmentId: string;
  note: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  decidedBy: { uid: string; name: string } | null;
  decidedAt: Timestamp | null;
  rejectReason: string | null;
}

export type AccountRequestRow = AccountRequest & { uid: string };

export interface SignupOrg {
  companies: { id: string; name: LocalizedText }[];
  administrations: { id: string; companyId: string; name: LocalizedText }[];
  departments: { id: string; companyId: string; administrationId: string; name: LocalizedText }[];
}

export interface RequestAccountInput {
  displayName: string;
  username: string;
  email: string;
  companyId: string;
  administrationId: string;
  departmentId: string;
  note: string;
  contactMethod: VerificationMethod;
  /** ID token of the throwaway SMS / email-link sign-in (lib/verify-auth). */
  contactProof: string;
  /** The hidden honeypot field's value ('' for people). */
  website?: string;
}

export interface ApproveAccountRequestInput {
  uid: string;
  password: string;
  displayName?: string;
  jobTitle?: string;
  staffNumber?: string;
  roleIds?: string[];
  companyId: string;
  administrationId: string;
  departmentId: string;
}

// ---------------------------------------------------------------------------
// Callables (created on first use so importing never touches Functions during prerender)
// ---------------------------------------------------------------------------

const listOrgFn = () => callable<Record<string, never>, SignupOrg>("listSignupOrgUnits")({});
const requestFn = (data: RequestAccountInput) => callable<RequestAccountInput, { status: "pending" }>("requestAccount")(data);
const statusFn = (data: { username: string }) =>
  callable<{ username: string }, { status: "pending" | "rejected" | "none" }>("accountRequestStatus")(data);
const approveFn = (data: ApproveAccountRequestInput) =>
  callable<ApproveAccountRequestInput, { uid: string }>("approveAccountRequest")(data);
const rejectFn = (data: { uid: string; reason: string }) =>
  callable<{ uid: string; reason: string }, { uid: string; status: "rejected" }>("rejectAccountRequest")(data);

const asText = (v: unknown): LocalizedText => {
  const o = (v ?? {}) as Record<string, unknown>;
  return { ar: typeof o.ar === "string" ? o.ar : "", en: typeof o.en === "string" ? o.en : "" };
};
const asStr = (v: unknown): string => (typeof v === "string" ? v : "");
const asList = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === "object") : [];

/** Every company / administration / department id with its names (works without signing in). */
export async function listSignupOrgUnits(): Promise<SignupOrg> {
  const raw = (await listOrgFn()) as unknown as Record<string, unknown>;
  return {
    companies: asList(raw?.companies).map((c) => ({ id: asStr(c.id), name: asText(c.name) })),
    administrations: asList(raw?.administrations).map((a) => ({
      id: asStr(a.id),
      companyId: asStr(a.companyId),
      name: asText(a.name),
    })),
    departments: asList(raw?.departments).map((d) => ({
      id: asStr(d.id),
      companyId: asStr(d.companyId),
      administrationId: asStr(d.administrationId),
      name: asText(d.name),
    })),
  };
}

/** Sends the request (the account stays disabled until an admin approves it). */
export function requestAccount(input: RequestAccountInput): Promise<{ status: "pending" }> {
  return requestFn({
    ...input,
    displayName: input.displayName.trim(),
    username: normalizeUsername(input.username),
    email: input.email.trim().toLowerCase(),
    note: input.note.trim(),
    website: input.website ?? "",
  });
}

export function approveAccountRequest(input: ApproveAccountRequestInput): Promise<{ uid: string }> {
  // Undefined keys would reach the callable as null: leave them out.
  const displayName = input.displayName?.trim();
  return approveFn({
    uid: input.uid,
    password: input.password,
    ...(displayName ? { displayName } : {}),
    jobTitle: input.jobTitle?.trim() ?? "",
    staffNumber: input.staffNumber?.trim() ?? "",
    roleIds: input.roleIds ?? [],
    companyId: input.companyId,
    administrationId: input.administrationId,
    departmentId: input.departmentId,
  });
}

export function rejectAccountRequest(input: { uid: string; reason?: string }): Promise<{ uid: string; status: "rejected" }> {
  return rejectFn({ uid: input.uid, reason: (input.reason ?? "").trim() });
}

// ---------------------------------------------------------------------------
// Login page helper
// ---------------------------------------------------------------------------

/** Sign-in failures after which it is worth asking whether the username has a request. */
const REQUEST_CHECK_CODES = new Set([
  "auth/user-disabled",
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
  "auth/user-not-found",
  "auth/wrong-password",
]);

/** 'pending' | 'rejected' | 'none' for a username (public, rate-limited per IP). */
export async function accountRequestStatus(username: string): Promise<"pending" | "rejected" | "none"> {
  const res = await statusFn({ username: normalizeUsername(username) });
  return res?.status === "pending" || res?.status === "rejected" ? res.status : "none";
}

/**
 * For the login page's error handling: when a sign-in failed because the
 * username belongs to a request that is still pending (its Auth user is
 * disabled) or was rejected, the message to show instead of the generic one
 * ({ title, body } keys in the requests namespace). Resolves null for any
 * other failure, a malformed username, or when the check itself fails; never
 * throws.
 */
export async function accountRequestNotice(
  err: unknown,
  username: string,
): Promise<{ title: string; body: string } | null> {
  const code = errorCode(err);
  if (!code || !REQUEST_CHECK_CODES.has(code) || validateUsername(username)) return null;
  try {
    const status = await accountRequestStatus(username);
    if (status === "pending") return { title: "requests.login.pendingTitle", body: "requests.login.pending" };
    if (status === "rejected") return { title: "requests.login.rejectedTitle", body: "requests.login.rejected" };
  } catch {
    // rate-limited or offline: keep the normal message
  }
  return null;
}

// ---------------------------------------------------------------------------
// Admin: live pending list
// ---------------------------------------------------------------------------

function toRow(uid: string, data: Record<string, unknown>): AccountRequestRow {
  return {
    uid,
    status: (data.status as AccountRequestStatus) ?? "pending",
    displayName: asStr(data.displayName),
    username: asStr(data.username),
    contactMethod: data.contactMethod === "email" ? "email" : "phone",
    contact: asStr(data.contact),
    contactVerifiedAt: (data.contactVerifiedAt as Timestamp) ?? null,
    email: asStr(data.email),
    companyId: asStr(data.companyId),
    administrationId: asStr(data.administrationId),
    departmentId: asStr(data.departmentId),
    note: asStr(data.note),
    createdAt: (data.createdAt as Timestamp) ?? null,
    updatedAt: (data.updatedAt as Timestamp) ?? null,
    decidedBy: (data.decidedBy as AccountRequest["decidedBy"]) ?? null,
    decidedAt: (data.decidedAt as Timestamp) ?? null,
    rejectReason: typeof data.rejectReason === "string" ? data.rejectReason : null,
  };
}

const millis = (t: Timestamp | null) => (t && typeof t.toMillis === "function" ? t.toMillis() : 0);

/**
 * Pending requests, live, newest first. At most ACCOUNT_REQUESTS_MAX_PENDING
 * (200) exist, so the whole set is read and sorted here (no index needed).
 * `enabled`: the viewer may create employees (the rules refuse anyone else).
 */
export function usePendingAccountRequests(enabled: boolean): {
  requests: AccountRequestRow[];
  loading: boolean;
  error: FirestoreError | null;
} {
  const [state, setState] = useState<{ rows: AccountRequestRow[]; ready: boolean; error: FirestoreError | null }>({
    rows: [],
    ready: false,
    error: null,
  });

  useEffect(() => {
    if (!enabled) return;
    const q = query(collection(db, ACCOUNT_REQUESTS_COLLECTION), where("status", "==", "pending"));
    return onSnapshot(
      q,
      (snap) =>
        setState({
          rows: snap.docs
            .map((d) => toRow(d.id, d.data(SNAPSHOT_OPTIONS)))
            .sort((a, b) => millis(b.createdAt) - millis(a.createdAt)),
          ready: true,
          error: null,
        }),
      (error) => setState({ rows: [], ready: true, error }),
    );
  }, [enabled]);

  return useMemo(
    () => ({
      requests: enabled ? state.rows : [],
      loading: enabled && !state.ready,
      error: enabled ? state.error : null,
    }),
    [enabled, state],
  );
}

/** How many requests are pending (0 while loading, on error or when not enabled). */
export function usePendingRequestCount(enabled: boolean): number {
  return usePendingAccountRequests(enabled).requests.length;
}

// ---------------------------------------------------------------------------
// Errors and helpers
// ---------------------------------------------------------------------------

/** Message key for a callable error: requests.errors.<reason>, else errors.<reason> / the code's message. */
export function requestErrorKey(err: unknown): { key: string; field: string | null } {
  const { reason, field } = callableErrorDetails(err);
  if (reason && hasMessage("en", `requests.errors.${reason}`)) return { key: `requests.errors.${reason}`, field };
  return { key: errorMessageKey(err), field };
}

/** A random 14-character password (letters and digits without look-alikes) for the approval dialog. */
export function generatePassword(length = 14): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}
